import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, ChevronDown } from "lucide-react";
import api from "../lib/api";

function formatNum(n) {
  return String(n);
}

export const Navbar = () => {
  const [episodes, setEpisodes] = useState([]);
  const [open, setOpen] = useState(false);
  const [logoGlow, setLogoGlow] = useState(false);
  const dropdownRef = useRef(null);
  const glowTimer = useRef(null);
  const canHover =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(hover: hover)").matches;

  useEffect(() => {
    api
      .get("/latest-episodes")
      .then((res) => setEpisodes(res.data || []))
      .catch(() => setEpisodes([]));
  }, []);

  useEffect(() => {
    const handleOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("click", handleOutside);
    return () => document.removeEventListener("click", handleOutside);
  }, []);

  const triggerGlow = () => {
    setLogoGlow(true);
    if (glowTimer.current) clearTimeout(glowTimer.current);
    glowTimer.current = setTimeout(() => setLogoGlow(false), 400);
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-2.5 group select-none"
          onTouchStart={triggerGlow}
          onMouseDown={triggerGlow}
        >
          <div
            className="w-9 h-9 rounded-lg bg-gradient-to-br from-rose-600 to-violet-600 flex items-center justify-center transition-[box-shadow] duration-200"
            style={{
              boxShadow: logoGlow
                ? "0 0 10px rgba(230,28,93,0.6), 0 0 20px rgba(230,28,93,0.35)"
                : "none",
            }}
          >
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <span className="font-display font-extrabold text-[clamp(13px,calc(9.6vw_-_19px),36px)] sm:text-4xl leading-none tracking-tight whitespace-nowrap">
            <span style={{ color: "#807E85" }}>Kahani</span>{" "}
            <span style={{ color: "#FF7A1A" }}>Originals</span>
            <span className="text-rose-500">.</span>
          </span>
        </Link>

        <nav className="flex items-center gap-2 sm:gap-4">
          <div
            ref={dropdownRef}
            className="relative"
            onMouseEnter={canHover ? () => setOpen(true) : undefined}
            onMouseLeave={canHover ? () => setOpen(false) : undefined}
          >
            <div className="flex items-center">
              <Link
                to="/"
                data-testid="nav-hub-link"
                className="text-sm font-medium text-gray-300 hover:text-white transition-colors pl-3 py-2"
              >
                Library
              </Link>
              <button
                type="button"
                data-testid="nav-library-dropdown-toggle"
                aria-expanded={open}
                aria-label="Show latest episodes"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen((v) => !v);
                }}
                className="text-gray-300 hover:text-white transition-colors pr-3 pl-1 py-2"
              >
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${
                    open ? "rotate-180" : ""
                  }`}
                  style={open ? { color: "#FF7A1A" } : undefined}
                />
              </button>
            </div>

            {open && (
              <div
                data-testid="nav-library-dropdown"
                className="absolute right-0 mt-3 w-64 rounded-xl border border-white/10 bg-[#161820] shadow-[0_16px_40px_rgba(0,0,0,0.6)] py-3 animate-in fade-in slide-in-from-top-1 duration-150"
              >
                <div className="px-4 pb-2 text-[11px] font-semibold uppercase tracking-wider text-white/40">
                  Latest Episode Drops
                </div>
                <ul className="list-none m-0 p-0">
                  {episodes.map((ep, i) => (
                    <li key={`${ep.series_slug}-${ep.chapter_number}-${i}`}>
                      <Link
                        to={`/series/${ep.series_slug}/chapter/${ep.chapter_number}`}
                        data-testid={`nav-episode-${ep.series_slug}-${formatNum(ep.chapter_number)}`}
                        onClick={() => setOpen(false)}
                        className="block px-4 py-2.5 text-sm text-white/85 hover:bg-white/5 hover:text-[#FF7A1A] transition-colors"
                      >
                        <span className="text-white/60 font-display font-bold mr-1.5">
                          Ch {formatNum(ep.chapter_number)}
                        </span>
                        {ep.series_title}
                      </Link>
                    </li>
                  ))}
                  <div className="h-px bg-white/[0.06] my-1.5" />
                  <li>
                    <Link
                      to="/"
                      data-testid="nav-browse-all-link"
                      onClick={() => setOpen(false)}
                      className="block px-4 py-2.5 text-sm font-medium text-rose-500 hover:bg-white/5 transition-colors"
                    >
                      Browse All Series &rarr;
                    </Link>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
};
